// Store Automation & Catalog Orders Hub (WP-12).
// Connects WooCommerce, Shopify, and Salla to recover abandoned carts, conduct COD verification, and manage catalog orders.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { StatCard } from '../components/ui/StatCard';
import { PillTabs } from '../components/ui/PillTabs';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import Toggle from '../components/ui/Toggle';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { OrderDetailDrawer } from '../components/store/OrderDetailDrawer';
import { ManualOrderModal } from '../components/store/ManualOrderModal';
import { StoreCampaignModal } from '../components/store/StoreCampaignModal';
import { StoreConnectionModal } from '../components/store/StoreConnectionModal';
import {
  listStoreConnections,
  deleteStoreConnection,
  listStoreCampaigns,
  saveStoreCampaign,
  deleteStoreCampaign,
  listCatalogOrders,
  CATALOG_ORDER_STATUSES,
  type StoreConnectionDoc,
  type StoreCampaignDoc,
  type CatalogOrderDoc,
  type CatalogOrderStatus,
} from '../lib/store';

type ActiveTab = 'orders' | 'campaigns' | 'connections';

export default function StoreAutomation() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState<ActiveTab>('orders');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [connections, setConnections] = useState<StoreConnectionDoc[]>([]);
  const [campaigns, setCampaigns] = useState<StoreCampaignDoc[]>([]);
  const [orders, setOrders] = useState<CatalogOrderDoc[]>([]);

  // Filter states for Orders
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals & Drawers states
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);

  const [manualOrderModalOpen, setManualOrderModalOpen] = useState(false);

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<StoreCampaignDoc | null>(null);

  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<StoreConnectionDoc | null>(null);

  const mountedRef = useRef(true);

  const loadAll = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [connsData, campsData, ordersData] = await Promise.all([
        listStoreConnections(),
        listStoreCampaigns(),
        listCatalogOrders({ limit: 100 }),
      ]);

      if (!mountedRef.current) return;
      setConnections(connsData);
      setCampaigns(campsData);
      setOrders(ordersData);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err.message || 'Failed to load store data');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadAll();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Stats computation
  const pendingOrdersCount = useMemo(() => {
    return orders.filter((o) => o.status === 'pending' || o.status === 'submitted').length;
  }, [orders]);

  const deliveredOrdersCount = useMemo(() => {
    return orders.filter((o) => o.status === 'delivered' || o.status === 'completed').length;
  }, [orders]);

  const totalRecoveredCarts = useMemo(() => {
    return campaigns.reduce((sum, c) => sum + (c.stats?.recoveredCount || 0), 0);
  }, [campaigns]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && o.source !== sourceFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = o.customerName.toLowerCase().includes(q);
        const matchesPhone = o.customerPhone.includes(q);
        const matchesNumber = o.orderNumber.toLowerCase().includes(q);
        if (!matchesName && !matchesPhone && !matchesNumber) return false;
      }
      return true;
    });
  }, [orders, statusFilter, sourceFilter, searchQuery]);

  // Order Actions
  const handleOpenOrderDetail = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOrderDrawerOpen(true);
  };

  // Campaign Actions
  const handleOpenCreateCampaign = () => {
    if (connections.length === 0) {
      toast.info(t('store.campaigns.needConnectionFirst'));
      setActiveTab('connections');
      return;
    }
    setEditingCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleOpenEditCampaign = (camp: StoreCampaignDoc) => {
    setEditingCampaign(camp);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campId: string) => {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('store.campaigns.deleteConfirm'), danger: true }))) return;
    try {
      await deleteStoreCampaign(campId);
      loadAll(true);
    } catch (err: any) {
      setError(err.message || 'Failed to delete campaign');
    }
  };

  const handleToggleCampaignEnabled = async (camp: StoreCampaignDoc) => {
    try {
      await saveStoreCampaign({
        ...camp,
        enabled: !camp.enabled,
      });
      loadAll(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update campaign');
    }
  };

  // Connection Actions
  const handleOpenCreateConnection = () => {
    setEditingConnection(null);
    setConnectionModalOpen(true);
  };

  const handleOpenEditConnection = (conn: StoreConnectionDoc) => {
    setEditingConnection(conn);
    setConnectionModalOpen(true);
  };

  const handleDeleteConnection = async (connId: string) => {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('store.connections.deleteConfirm'), confirmLabel: t('common.remove'), danger: true }))) return;
    try {
      await deleteStoreConnection(connId);
      loadAll(true);
    } catch (err: any) {
      setError(err.message || 'Failed to delete connection');
    }
  };

  const getStatusBadgeVariant = (status: CatalogOrderStatus): 'success' | 'warning' | 'info' | 'slate' | 'danger' => {
    switch (status) {
      case 'delivered':
      case 'completed':
      case 'approved':
        return 'success';
      case 'pending':
      case 'submitted':
        return 'warning';
      case 'shipped':
        return 'info';
      case 'rejected':
      case 'cancelled':
      case 'refunded':
        return 'danger';
      default:
        return 'slate';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 pb-12">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
        </div>
        {/* 4 StatCards Skeleton */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Skeleton.StatCard />
          <Skeleton.StatCard />
          <Skeleton.StatCard />
          <Skeleton.StatCard />
        </div>
        {/* Tabs Skeleton */}
        <div className="flex gap-2 border-b border-line pb-2 dark:border-slate-800">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
        </div>
        {/* Table / Cards Skeleton */}
        <div className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton.TableRow key={i} cols={8} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-ink dark:text-white">
            {t('store.title')}
          </h1>
          <p className="text-xs text-ink-3 dark:text-slate-400">
            {t('store.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => loadAll(true)}
            loading={refreshing}
          >
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 p-4 text-xs text-danger dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-400">
          {error}
        </div>
      )}

      {/* 4 StatCards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label={t('store.stats.totalOrders')}
          value={orders.length}
          variant="indigo"
        />
        <StatCard
          label={t('store.stats.pendingSubmitted')}
          value={pendingOrdersCount}
          variant="amber"
        />
        <StatCard
          label={t('store.stats.deliveredCompleted')}
          value={deliveredOrdersCount}
          variant="green"
        />
        <StatCard
          label={t('store.stats.cartsRecovered')}
          value={totalRecoveredCarts}
          variant="sky"
        />
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line pb-2 dark:border-slate-800">
        <PillTabs
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as ActiveTab)}
          tabs={[
            { id: 'orders', label: t('store.tabs.orders'), count: orders.length },
            { id: 'campaigns', label: t('store.tabs.campaigns'), count: campaigns.length },
            { id: 'connections', label: t('store.tabs.connections'), count: connections.length },
          ]}
        />
      </div>

      {/* Tab 1: Orders */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('store.orders.searchPlaceholder')}
                className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white w-48 sm:w-60"
              />

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              >
                <option value="all">{t('store.orders.allStatuses')}</option>
                {CATALOG_ORDER_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {t(`store.status.${st}`)}
                  </option>
                ))}
              </select>

              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              >
                <option value="all">{t('store.orders.allSources')}</option>
                <option value="woocommerce">WooCommerce</option>
                <option value="shopify">Shopify</option>
                <option value="salla">Salla</option>
                <option value="whatsapp_catalog">WhatsApp Catalog</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            <Button
              size="sm"
              variant="primary"
              onClick={() => setManualOrderModalOpen(true)}
            >
              + {t('store.orders.createManual')}
            </Button>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface p-8 shadow-sm dark:border-slate-800">
              <EmptyState
                title={t('store.orders.empty')}
                description={t('store.orders.emptyDesc')}
                action={
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setManualOrderModalOpen(true)}
                  >
                    + {t('store.orders.createManual')}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-line bg-surface shadow-sm dark:border-slate-800 dark:bg-slate-850">
              <table className="w-full text-start text-xs">
                <thead>
                  <tr className="border-b border-line text-ink-3 dark:border-slate-800 dark:text-slate-400">
                    <th className="py-2.5 pe-4 ps-3 text-start font-semibold">
                      {t('store.orders.orderNumber')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">
                      {t('store.orders.customer')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">
                      {t('store.orders.source')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">
                      {t('store.orders.totalAmount')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">
                      {t('store.orders.paymentStatus')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">
                      {t('store.orders.codStatus')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">
                      {t('store.orders.status')}
                    </th>
                    <th className="py-2.5 px-3 text-start font-semibold">Date</th>
                    <th className="py-2.5 pe-3 ps-3 text-end font-semibold">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line dark:divide-slate-800">
                  {filteredOrders.map((o) => (
                    <tr
                      key={o.id}
                      onClick={() => handleOpenOrderDetail(o.id)}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="py-3 pe-4 ps-3 font-mono font-bold text-ink dark:text-white">
                        #{o.orderNumber}
                      </td>
                      <td className="py-3 px-3">
                        <span className="block font-semibold text-ink dark:text-white">
                          {o.customerName}
                        </span>
                        <span className="font-mono text-[11px] text-ink-3 dark:text-slate-400">
                          {o.customerPhone}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-700 uppercase dark:bg-slate-800 dark:text-slate-300">
                          {o.source}
                        </span>
                      </td>
                      <td className="py-3 px-3 font-bold text-ink dark:text-white">
                        {o.totalAmount.toFixed(2)} {o.currency}
                      </td>
                      <td className="py-3 px-3">
                        <Badge
                          variant={
                            o.paymentStatus === 'paid'
                              ? 'success'
                              : o.paymentStatus === 'refunded'
                              ? 'danger'
                              : 'warning'
                          }
                        >
                          {o.paymentStatus}
                        </Badge>
                      </td>
                      <td className="py-3 px-3">
                        {o.codStatus ? (
                          <Badge
                            variant={
                              o.codStatus === 'confirmed'
                                ? 'success'
                                : o.codStatus === 'cancelled'
                                ? 'danger'
                                : 'warning'
                            }
                          >
                            {o.codStatus}
                          </Badge>
                        ) : (
                          <span className="text-ink-3 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <Badge variant={getStatusBadgeVariant(o.status)}>
                          {t(`store.status.${o.status}`)}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-[11px] text-ink-3 dark:text-slate-400">
                        {new Date(o.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 pe-3 ps-3 text-end" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenOrderDetail(o.id)}
                        >
                          {t('store.orders.details')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Campaigns */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-ink dark:text-white">
              {t('store.tabs.campaigns')} ({campaigns.length})
            </h2>
            <Button size="sm" variant="primary" onClick={handleOpenCreateCampaign}>
              + {t('store.campaigns.create')}
            </Button>
          </div>

          {campaigns.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface p-8 shadow-sm dark:border-slate-800">
              <EmptyState
                title={t('store.campaigns.empty')}
                description={t('store.campaigns.emptyDesc')}
                action={
                  <Button size="sm" variant="primary" onClick={handleOpenCreateCampaign}>
                    + {t('store.campaigns.create')}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {campaigns.map((camp) => (
                <div
                  key={camp.id}
                  className="flex flex-col justify-between rounded-xl border border-line bg-surface p-5 shadow-sm transition hover:shadow dark:border-slate-800 dark:bg-slate-850"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-ink dark:text-white">
                            {camp.name}
                          </h3>
                          <Badge variant={camp.enabled ? 'success' : 'slate'}>
                            {camp.enabled ? t('store.campaigns.enabled') : t('store.campaigns.disabled')}
                          </Badge>
                        </div>
                        <span className="text-xs text-ink-3 dark:text-slate-400">
                          {t(`store.ops.${camp.operation}`)} · {camp.storeType.toUpperCase()}
                        </span>
                      </div>
                      <Toggle
                        on={camp.enabled}
                        onChange={() => handleToggleCampaignEnabled(camp)}
                        label={camp.name}
                      />
                    </div>

                    <div className="rounded-lg bg-surface-subtle p-3 text-xs dark:bg-slate-900/60 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-ink-3 dark:text-slate-400">{t('store.campaigns.templateId')}:</span>
                        <span className="font-mono font-semibold text-brand dark:text-brand-tint">{camp.templateId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-ink-3 dark:text-slate-400">{t('store.campaigns.delayMin')}:</span>
                        <span className="font-semibold text-ink dark:text-white">{camp.delayMin} min</span>
                      </div>
                      {camp.couponCode && (
                        <div className="flex justify-between">
                          <span className="text-ink-3 dark:text-slate-400">{t('store.campaigns.couponCode')}:</span>
                          <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                            {camp.couponCode} ({camp.couponDiscount || 'Discount'})
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between border-t border-line pt-2 text-xs text-ink-3 dark:border-slate-800 dark:text-slate-400">
                      <span>
                        Sent: <b>{camp.stats?.sentCount || 0}</b> · Recovered: <b>{camp.stats?.recoveredCount || 0}</b>
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenEditCampaign(camp)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteCampaign(camp.id)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Connections */}
      {activeTab === 'connections' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-ink dark:text-white">
              {t('store.tabs.connections')} ({connections.length})
            </h2>
            <Button size="sm" variant="primary" onClick={handleOpenCreateConnection}>
              + {t('store.connections.create')}
            </Button>
          </div>

          {connections.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface p-8 shadow-sm dark:border-slate-800">
              <EmptyState
                title={t('store.connections.empty')}
                description={t('store.connections.emptyDesc')}
                action={
                  <Button size="sm" variant="primary" onClick={handleOpenCreateConnection}>
                    + {t('store.connections.create')}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {connections.map((conn) => (
                <div
                  key={conn.id}
                  className="flex flex-col justify-between rounded-xl border border-line bg-surface p-5 shadow-sm transition hover:shadow dark:border-slate-800 dark:bg-slate-850"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-ink dark:text-white">
                            {conn.name}
                          </h3>
                          <Badge variant={conn.status === 'active' ? 'success' : 'slate'}>
                            {conn.status === 'active' ? t('status.active') : t('store.connections.inactive')}
                          </Badge>
                        </div>
                        <a
                          href={conn.storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-mono text-brand hover:underline dark:text-brand-tint"
                        >
                          {conn.storeUrl}
                        </a>
                      </div>
                      <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 uppercase dark:bg-slate-800 dark:text-slate-300">
                        {conn.storeType}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 rounded-lg bg-surface-subtle p-3 text-center text-xs dark:bg-slate-900/60">
                      <div>
                        <span className="block text-ink-3 dark:text-slate-400">{t('store.stats.orders')}</span>
                        <b className="text-ink dark:text-white">{conn.stats?.totalOrders || 0}</b>
                      </div>
                      <div>
                        <span className="block text-ink-3 dark:text-slate-400">{t('store.stats.abandoned')}</span>
                        <b className="text-amber-600 dark:text-amber-400">{conn.stats?.abandonedCarts || 0}</b>
                      </div>
                      <div>
                        <span className="block text-ink-3 dark:text-slate-400">COD Confirmed</span>
                        <b className="text-emerald-600 dark:text-emerald-400">{conn.stats?.codConfirmed || 0}</b>
                      </div>
                    </div>

                    <div className="rounded-lg border border-line p-2 text-[11px] dark:border-slate-800">
                      <span className="block font-semibold text-ink-3 dark:text-slate-400">
                        {t('store.connections.webhookEndpoint')}:
                      </span>
                      <code className="mt-0.5 block break-all font-mono text-[10px] text-brand dark:text-brand-tint">
                        {`https://wapp.tagnologys.com/store/webhook/${conn.tenantId || '{tenantId}'}/${conn.id}`}
                      </code>
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-line dark:border-slate-800">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/store/${conn.id}/invoice`)}
                      >
                        🧾 {t('store.connections.invoice')}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleOpenEditConnection(conn)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteConnection(conn.id)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals & Drawers */}
      <OrderDetailDrawer
        open={orderDrawerOpen}
        orderId={selectedOrderId}
        onClose={() => setOrderDrawerOpen(false)}
        onUpdated={() => loadAll(true)}
      />

      <ManualOrderModal
        open={manualOrderModalOpen}
        onClose={() => setManualOrderModalOpen(false)}
        onCreated={() => loadAll(true)}
      />

      <StoreCampaignModal
        open={campaignModalOpen}
        campaign={editingCampaign}
        connections={connections}
        onClose={() => setCampaignModalOpen(false)}
        onSaved={() => loadAll(true)}
      />

      <StoreConnectionModal
        open={connectionModalOpen}
        connection={editingConnection}
        onClose={() => setConnectionModalOpen(false)}
        onSaved={() => loadAll(true)}
      />
    </div>
  );
}
